(() => {
  const existing = window.NaveroCurrency;
  if (existing && typeof existing.scan === 'function') {
    existing.scan(document);
    return;
  }

  const config = window.NaveroCurrencyConfig || {};
  const storageKey = 'navero_display_currency';
  let cartEstimateTimer;
  let cartEstimateRunning = false;

  const enabled = config.enabled === true;
  const country = String(config.country || '').toUpperCase();
  const rate = Number(config.usdRate);
  const rounding = String(config.rounding || '1');
  const storeCurrency = String(config.storeCurrency || '').toUpperCase();

  const getSavedCurrency = () => {
    try {
      const saved = window.localStorage.getItem(storageKey);
      return saved === 'USD' || saved === 'VND' ? saved : null;
    } catch (error) {
      return null;
    }
  };

  let displayCurrency = getSavedCurrency() || (country === 'US' ? 'USD' : 'VND');

  const canUseUsd = () => enabled && storeCurrency === 'VND' && Number.isFinite(rate) && rate > 0;
  const shouldDisplayUsd = () => canUseUsd() && displayCurrency === 'USD';

  const formatUsd = (value, roundingRule = rounding) => {
    let amount = value;
    if (roundingRule && roundingRule !== 'none') {
      const step = Number(roundingRule);
      if (Number.isFinite(step) && step > 0) {
        amount = Math.round(amount / step) * step;
      }
    }

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: roundingRule === 'none' ? 2 : 0,
      maximumFractionDigits: roundingRule === 'none' ? 2 : 0,
    }).format(amount);
  };

  const subunitsToVnd = (value) => Number(value) / 100;

  const renderMoneyNode = (node) => {
    if (!(node instanceof HTMLElement)) return;

    const rawCents = Number(node.dataset.naveroVndCents);
    const nodeRate = Number(node.dataset.naveroUsdRate || rate);
    const nodeRounding = node.dataset.naveroUsdRounding || rounding;

    if (!Number.isFinite(rawCents)) return;

    if (node.dataset.naveroOriginalText === undefined) {
      node.dataset.naveroOriginalText = node.textContent || '';
    }

    if (!shouldDisplayUsd() || !Number.isFinite(nodeRate) || nodeRate <= 0) {
      node.textContent = node.dataset.naveroOriginalText;
      node.dataset.naveroConverted = 'false';
      return;
    }

    const usdAmount = subunitsToVnd(rawCents) / nodeRate;
    const fromPrefix = node.dataset.naveroFromPrice === 'true' ? 'From ' : '';
    node.textContent = `${fromPrefix}≈ ${formatUsd(usdAmount, nodeRounding)} USD`;
    node.dataset.naveroConverted = 'true';
  };

  const scan = (root = document) => {
    if (!root || typeof root.querySelectorAll !== 'function') return;

    if (root instanceof HTMLElement && root.matches('[data-navero-money]')) {
      renderMoneyNode(root);
    }

    root.querySelectorAll('[data-navero-money]').forEach(renderMoneyNode);
  };

  const clearCartEstimates = () => {
    document.querySelectorAll('[data-navero-cart-estimate]').forEach((node) => node.remove());
  };

  const renderCartEstimate = async () => {
    if (!shouldDisplayUsd() || cartEstimateRunning) {
      if (!shouldDisplayUsd()) clearCartEstimates();
      return;
    }

    const totalNodes = document.querySelectorAll('.totals__total-value');
    if (!totalNodes.length) return;

    cartEstimateRunning = true;

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) return;

      const cart = await response.json();
      const usdTotal = subunitsToVnd(cart.total_price) / rate;
      const estimateText = `≈ ${formatUsd(usdTotal)} USD · Checkout is processed in VND`;

      totalNodes.forEach((totalNode) => {
        let estimate = totalNode.parentElement?.querySelector('[data-navero-cart-estimate]');
        if (!estimate) {
          estimate = document.createElement('small');
          estimate.setAttribute('data-navero-cart-estimate', '');
          estimate.style.display = 'block';
          estimate.style.marginTop = '0.4rem';
          estimate.style.opacity = '0.7';
          estimate.style.fontSize = '0.85em';
          totalNode.insertAdjacentElement('afterend', estimate);
        }

        if (estimate.textContent !== estimateText) {
          estimate.textContent = estimateText;
        }
      });
    } catch (error) {
      // Keep Shopify's original VND cart display untouched if estimation fails.
    } finally {
      cartEstimateRunning = false;
    }
  };

  const scheduleCartEstimate = () => {
    window.clearTimeout(cartEstimateTimer);
    cartEstimateTimer = window.setTimeout(renderCartEstimate, 120);
  };

  const setDisplayCurrency = (currency) => {
    if (currency !== 'USD' && currency !== 'VND') return;
    displayCurrency = currency;

    try {
      window.localStorage.setItem(storageKey, currency);
    } catch (error) {
      // Local storage is optional; country default still works without it.
    }

    document.querySelectorAll('[data-navero-currency-selector]').forEach((select) => {
      select.value = currency;
    });

    scan(document);
    scheduleCartEstimate();
  };

  const mountSelector = () => {
    if (!enabled || storeCurrency !== 'VND' || document.querySelector('[data-navero-currency-selector]')) return;

    const headerIcons = document.querySelector('.header__icons');
    if (!headerIcons) return;

    const wrapper = document.createElement('div');
    wrapper.setAttribute('data-navero-currency-selector-wrapper', '');
    wrapper.style.display = 'flex';
    wrapper.style.alignItems = 'center';
    wrapper.style.marginRight = '0.8rem';

    const select = document.createElement('select');
    select.setAttribute('data-navero-currency-selector', '');
    select.setAttribute('aria-label', 'Display currency');
    select.style.font = 'inherit';
    select.style.background = 'transparent';
    select.style.color = 'inherit';
    select.style.border = '1px solid rgba(var(--color-foreground), 0.2)';
    select.style.borderRadius = '999px';
    select.style.padding = '0.6rem 2.4rem 0.6rem 1rem';
    select.innerHTML = '<option value="VND">VND</option><option value="USD">USD</option>';
    select.value = displayCurrency;
    select.addEventListener('change', (event) => setDisplayCurrency(event.target.value));

    wrapper.appendChild(select);
    headerIcons.prepend(wrapper);
  };

  window.NaveroCurrency = {
    scan,
    renderCartEstimate,
    setDisplayCurrency,
    getDisplayCurrency: () => displayCurrency,
  };

  const start = () => {
    mountSelector();
    scan(document);
    scheduleCartEstimate();

    const observer = new MutationObserver((mutations) => {
      let shouldRefreshCart = false;
      let shouldMountSelector = false;

      for (const mutation of mutations) {
        mutation.addedNodes.forEach((addedNode) => {
          if (addedNode instanceof HTMLElement) {
            scan(addedNode);

            if (addedNode.matches?.('.header__icons') || addedNode.querySelector?.('.header__icons')) {
              shouldMountSelector = true;
            }

            if (
              addedNode.matches?.('.js-contents, .cart-drawer, #main-cart-footer, .totals') ||
              addedNode.querySelector?.('.totals__total-value')
            ) {
              shouldRefreshCart = true;
            }
          }
        });
      }

      if (shouldMountSelector) mountSelector();
      if (shouldRefreshCart) scheduleCartEstimate();
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
